import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import type { FormEventHandler, MouseEventHandler } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

export const EMAIL_FIELD = 'login';
export const PASSWORD_FIELD = 'password';

export interface LoginFormProps {
  onSubmitHandler: FormEventHandler<HTMLFormElement>;
  onForgotPassword: MouseEventHandler<HTMLElement>;
}

export interface FormValues {
  [EMAIL_FIELD]: string;
  [PASSWORD_FIELD]: string;
}

export const validationSchema = object().shape({
  [EMAIL_FIELD]: string().email('Please enter a valid email address').required('Email is required'),
  [PASSWORD_FIELD]: string().required('Password is required'),
});

export const LoginForm = ({ onSubmitHandler, onForgotPassword }: LoginFormProps) => {
  const { t } = useTranslation();
  const {
    formState: { isSubmitting },
  } = useFormContext();

  return (
    <>
      <Typography
        variant="h2"
        gutterBottom
        data-test="login-text"
        sx={{
          '&:before': {
            borderTop: theme => `1px solid ${theme.palette.primary.main}`,
            content: '""',
            display: 'block',
            marginBottom: '8px',
            width: '12px',
          },
        }}
      >
        {t('Log in')}
      </Typography>
      <form onSubmit={onSubmitHandler} data-test="login-form">
        <Grid container spacing={3}>
          <Grid size={12}>
            <RHFTextField
              name={EMAIL_FIELD}
              type="email"
              label="Email"
              placeholder="Enter your email"
              required
              fullWidth
              margin="dense"
              autoComplete="username"
              dataTest="email"
            />
            <RHFTextField
              name={PASSWORD_FIELD}
              type="password"
              label="Password"
              placeholder="Enter your password"
              required
              fullWidth
              margin="dense"
              autoComplete="current-password"
              dataTest="password"
            />
          </Grid>
          <Grid size={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              sx={{
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Grid>
                <Button variant="text" onClick={onForgotPassword} data-test="forgot-password">
                  Forgot Password
                </Button>
              </Grid>
              <Grid>
                <Button
                  type="submit"
                  color="primary"
                  loading={isSubmitting}
                  data-test="sign-in-button"
                  disabled={isSubmitting}
                >
                  Sign in
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </>
  );
};
